import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, RefreshCw, CheckCircle2, XCircle, MinusCircle, ExternalLink, Clock, Search, MessageCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { DeliveryHealth, DeliveryHealthButton } from "./DeliveryHealth";
import { WaStatus } from "./WhatsAppPanel";
import { showroomLabel, stayLabel } from "./ShowroomBlock";

const BRAND_LABEL = { unitile: "Unitile", univicoustic: "UniVicoustic", uflex: "U-Flex" };

function Status({ d }) {
  const t = d?.tracking;
  if (d?.status === "sent") {
    if (t?.bad) return <span className="inline-flex items-center gap-1 text-red-600 text-xs" title={t.reason || ""}><XCircle className="h-3.5 w-3.5" /> {t.state[0].toUpperCase() + t.state.slice(1)}</span>;
    if (t && t.event !== "requests") return <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> {t.state[0].toUpperCase() + t.state.slice(1)}{d.attached?.length ? ` · ${d.attached.length} PDF` : ""}</span>;
    return <span className="inline-flex items-center gap-1 text-sky-700 text-xs" title="Brevo accepted the email for sending; delivery not confirmed yet"><Clock className="h-3.5 w-3.5" /> Accepted by Brevo{d.attached?.length ? ` · ${d.attached.length} PDF` : ""}</span>;
  }
  if (d?.status === "failed") return <span className="inline-flex items-center gap-1 text-red-600 text-xs" title={d.error}><XCircle className="h-3.5 w-3.5" /> Failed</span>;
  return <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><MinusCircle className="h-3.5 w-3.5" /> Not sent</span>;
}

export function LeadRegister({ brand }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [event, setEvent] = useState("");
  const [scope, setScope] = useState(() => (["marketing", "super_admin", "sales_head", "management"].includes(user?.role) ? "team" : "mine"));
  const [busy, setBusy] = useState(null);
  const [health, setHealth] = useState(false);
  const params = { event, brand: brand || "", scope };
  const { data, isLoading } = useQuery({ queryKey: ["ld-register", params], queryFn: async () => (await api.get("/lead-desk/register", { params })).data });
  const rows = data?.rows || [];

  const resendWa = async (id) => {
    setBusy(`wa-${id}`);
    try {
      const { data: r } = await api.post(`/lead-desk/captures/${id}/resend-whatsapp`);
      r.whatsapp.status === "sent" ? toast.success("Sent on WhatsApp") : toast.error(r.whatsapp.error || "WhatsApp send failed");
      qc.invalidateQueries({ queryKey: ["ld-register"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const resend = async (id) => {
    setBusy(id);
    try {
      const { data: r } = await api.post(`/lead-desk/captures/${id}/resend`);
      r.delivery.status === "sent" ? toast.success("Catalogues re-sent") : toast.error(r.delivery.error || "Send failed");
      qc.invalidateQueries({ queryKey: ["ld-register"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const checkDelivery = async (id) => {
    setBusy(`chk-${id}`);
    try {
      const { data: r } = await api.post(`/lead-desk/captures/${id}/check-delivery`);
      const t = r.delivery?.tracking;
      (t?.bad ? toast.error : toast.success)(`Brevo says: ${t?.state || "no events yet"}${t?.reason ? ` — ${t.reason}` : ""}`);
      qc.invalidateQueries({ queryKey: ["ld-register"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/lead-desk/register/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `lead_register_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const s = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="lead-register">
      <div className="flex flex-wrap items-center gap-3">
        {[["Captured leads", s.captured], ["Accepted by Brevo", s.sent], ["Failed", s.failed], ["Catalogues matched", s.catalogues]].map(([l, v]) => (
          <div key={l} className="rounded-md border border-slate-200 bg-white px-3 py-2 min-w-[130px]"><p className="text-[10px] uppercase tracking-widest text-slate-400">{l}</p><p className="font-mono text-xl font-semibold text-slate-900" data-testid={`ld-stat-${l.split(" ")[0].toLowerCase()}`}>{v ?? 0}</p></div>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {data?.can_team && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="ld-register-scope"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="mine">My captures</SelectItem><SelectItem value="team">{user?.role === "marketing" || user?.role === "super_admin" || user?.role === "sales_head" ? "All reps" : "My team"}</SelectItem></SelectContent>
            </Select>
          )}
          <Select value={event || "all"} onValueChange={(v) => setEvent(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="ld-register-event"><SelectValue placeholder="All exhibitions" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All exhibitions</SelectItem>{(data?.events || []).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" className="h-9 gap-1.5" onClick={exportXlsx} data-testid="ld-register-export"><FileSpreadsheet className="h-4 w-4" /> Export Excel</Button>
          <DeliveryHealthButton onClick={() => setHealth((v) => !v)} />
        </div>
      </div>
      {health && <DeliveryHealth onClose={() => setHealth(false)} />}
      <Card className="border-slate-200 overflow-x-auto">
        {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50/70">
              {["Contact", "Interest", "Exhibition", "Delivery", "Captured", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`ld-row-${r.id}`}>
                  <TableCell>
                    <p className="font-medium text-slate-800">{r.name}{r.is_sample && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200" data-testid={`ld-sample-${r.id}`}>Sample</span>}</p>
                    <p className="text-xs text-slate-500">{[r.designation, r.company].filter(Boolean).join(" · ")}</p>
                    <p className="text-xs text-slate-400">{[r.email, r.phone].filter(Boolean).join(" · ")}</p>
                  </TableCell>
                  <TableCell className="max-w-[260px]">
                    <p className="text-xs text-slate-700">{r.interests?.length ? r.interests.map((i) => i.name).join(", ") : <span className="text-slate-400">—</span>}</p>
                    {r.notes && <p className="text-[11px] text-slate-400 truncate" title={r.notes}>{r.notes}</p>}
                    {(r.showroom_interest || r.stay_connected?.length > 0) && (
                      <p className="text-[11px] mt-1 flex flex-wrap gap-1" data-testid={`ld-showroom-${r.id}`}>
                        {r.showroom_interest && <span className={`rounded px-1.5 py-0.5 ${r.showroom_interest === "yes" ? "bg-emerald-50 text-emerald-700" : r.showroom_interest === "later" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>Showroom: {showroomLabel(r.showroom_interest)}{r.showroom_locations?.length ? ` · ${r.showroom_locations.join(", ")}` : ""}</span>}
                        {(r.stay_connected || []).map((k) => <span key={k} className="rounded bg-sky-50 text-sky-700 px-1.5 py-0.5">{stayLabel(k)}</span>)}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap">{r.event_name}<br /><span className="text-slate-400">{BRAND_LABEL[r.brand]}</span></TableCell>
                  <TableCell>
                    <Status d={r.delivery} />
                    {r.whatsapp && r.whatsapp.status !== "not_sent" && <div><WaStatus w={r.whatsapp} testid={`ld-wa-${r.id}`} /></div>}
                    {!!r.catalogue_opens?.length && <p className="text-[10px] text-emerald-700" data-testid={`ld-opens-${r.id}`}>Opened {r.catalogue_opens.length}× · {[...new Set(r.catalogue_opens.map((o) => o.catalogue))].join(", ")}</p>}
                    {r.delivery?.at && <p className="text-[10px] text-slate-400">{new Date(r.delivery.tracking?.at || r.delivery.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>}
                    {r.delivery?.status === "failed" && r.delivery.error && <p className="text-[10px] text-red-500 max-w-[220px] break-words" data-testid={`ld-error-${r.id}`}>{r.delivery.error}</p>}
                    {r.delivery?.tracking?.note && <p className="text-[10px] text-amber-600 max-w-[180px]">{r.delivery.tracking.note}</p>}
                    {r.delivery?.message_id && (
                      <button onClick={() => checkDelivery(r.id)} disabled={busy === `chk-${r.id}`} className="text-[10px] text-sky-700 hover:underline inline-flex items-center gap-1 mt-0.5" data-testid={`ld-check-${r.id}`}>
                        {busy === `chk-${r.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />} Check with Brevo
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}<br /><span className="text-slate-400">{r.user_name}</span></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Link to={`/leads/${r.lead_id}`} className="text-indigo-600 hover:text-indigo-800 text-xs inline-flex items-center gap-1 mr-3" data-testid={`ld-open-lead-${r.id}`}><ExternalLink className="h-3 w-3" /> Lead</Link>
                    {r.email && <button onClick={() => resend(r.id)} disabled={busy === r.id} className="text-slate-500 hover:text-slate-900 text-xs inline-flex items-center gap-1" data-testid={`ld-resend-${r.id}`}>{busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Resend</button>}
                    {r.phone && <button onClick={() => resendWa(r.id)} disabled={busy === `wa-${r.id}`} className="text-emerald-700 hover:text-emerald-900 text-xs inline-flex items-center gap-1 ml-2" data-testid={`ld-resend-wa-${r.id}`}>{busy === `wa-${r.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircle className="h-3 w-3" />} WhatsApp</button>}
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-10">Your next conversation starts here — capture a lead to see its matched catalogues and delivery record.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
